import React, { lazy, useCallback, useState } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { IdentificationModalWrapper, ModalOptions } from '@fiverr-private/identification_core';
import { Experiments, AllocationGroups } from '../../shared/constants/experiments';
import { StepId } from '../../shared/constants/stepId';

const LazyIdentificationForm = lazy(
    () => import(/* webpackChunkName: "LazyIdentificationForm" */ '../../components/IdentificationForm')
);

export interface IdentificationModalProps {
    googleClientId: string;
    autoShowSignUpEnabled?: boolean;
    inLynxesIdentificationResetPasswordRollout?: boolean;
    inLynxesIdentificationIllustrationRollout?: boolean;
    inLynxesGoogleOneTapFedCmRollout?: boolean;
    variant?: ModalOptions['variant'];
    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout?: boolean;
    inBeetlesMigrateSignupWebRollout: boolean;
    inBeetlesMigrateSigninWebRollout: boolean;
    inBeetlesMigrateSocialSignUpRollout: boolean;
    inLynxesRemoveQuestionnaireFromBlockedActionsRollout?: boolean;
    inCaribouSsoPromotionExperimentRollout?: boolean;
}

export const IdentificationModal = ({
    googleClientId,
    autoShowSignUpEnabled,
    inLynxesIdentificationResetPasswordRollout,
    inLynxesIdentificationIllustrationRollout,
    inLynxesGoogleOneTapFedCmRollout,
    variant: variantProp,
    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout,
    inBeetlesMigrateSignupWebRollout,
    inBeetlesMigrateSigninWebRollout,
    inBeetlesMigrateSocialSignUpRollout,
    inLynxesRemoveQuestionnaireFromBlockedActionsRollout,
    inCaribouSsoPromotionExperimentRollout,
}: IdentificationModalProps) => {
    const { abTests } = getContext();
    const serverExperimentGroup = Number(abTests[Experiments.CARIBOU_SSO_PROMOTION_EXPERIMENT]) || undefined;
    // Track allocated group via callback (for client-side allocation)
    const [allocatedGroup, setAllocatedGroup] = useState<number | undefined>(serverExperimentGroup);
    const experimentGroup = allocatedGroup ?? serverExperimentGroup;
    const inGroupC = inCaribouSsoPromotionExperimentRollout && experimentGroup === AllocationGroups.C;

    const [closeOnOverlayClick, setCloseOnOverlayClick] = useState(true);

    const onStepIdChange = useCallback(
        (stepId: StepId) => {
            // Group C: disable overlay click only on code_activation step
            if (inGroupC && stepId === 'code_activation') {
                setCloseOnOverlayClick(false);
            } else {
                setCloseOnOverlayClick(true);
            }
        },
        [inGroupC]
    );

    return (
        <IdentificationModalWrapper
            googleClientId={googleClientId}
            autoShowSignUpEnabled={autoShowSignUpEnabled}
            closeOnOverlayClick={closeOnOverlayClick}
            variant={variantProp}
            inLynxesGoogleOneTapFedCmRollout={inLynxesGoogleOneTapFedCmRollout}
            onSsoPromotionExperimentGroupAllocated={setAllocatedGroup}
            renderLazyComponent={({
                isModal,
                isModalOpen,
                closeModal,
                mode,
                variant,
                triggerType,
                redirectUrl,
                email,
                username,
                isSticky,
                skipQuestionnaire,
                blockedActionType,
                bigQueryEnrichment,
                mixpanelEnrichment,
                afterSigningSuccess,
                isSocialFlow,
                stepId,
                skipActivation,
                isCodeActivationSkippable,
                onSsoPromotionExperimentGroupAllocated,
            }) => (
                <LazyIdentificationForm
                    isModal={isModal}
                    isModalOpen={isModalOpen}
                    closeModal={closeModal}
                    mode={mode}
                    variant={variant}
                    triggerType={triggerType}
                    redirectUrl={redirectUrl}
                    email={email}
                    username={username}
                    isSticky={isSticky}
                    skipQuestionnaire={skipQuestionnaire}
                    blockedActionType={blockedActionType}
                    mixpanelEnrichment={mixpanelEnrichment}
                    bigQueryEnrichment={bigQueryEnrichment}
                    afterSigningSuccess={afterSigningSuccess}
                    isSocialFlow={isSocialFlow}
                    stepId={stepId}
                    skipActivation={skipActivation}
                    isCodeActivationSkippable={isCodeActivationSkippable}
                    inLynxesIdentificationResetPasswordRollout={inLynxesIdentificationResetPasswordRollout}
                    inLynxesIdentificationIllustrationRollout={inLynxesIdentificationIllustrationRollout}
                    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout={
                        inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout
                    }
                    inBeetlesMigrateSignupWebRollout={inBeetlesMigrateSignupWebRollout}
                    inBeetlesMigrateSigninWebRollout={inBeetlesMigrateSigninWebRollout}
                    inBeetlesMigrateSocialSignUpRollout={inBeetlesMigrateSocialSignUpRollout}
                    inLynxesRemoveQuestionnaireFromBlockedActionsRollout={
                        inLynxesRemoveQuestionnaireFromBlockedActionsRollout
                    }
                    inCaribouSsoPromotionExperimentRollout={inCaribouSsoPromotionExperimentRollout}
                    onStepIdChange={onStepIdChange}
                    onSsoPromotionExperimentGroupAllocated={onSsoPromotionExperimentGroupAllocated}
                />
            )}
        />
    );
};
